import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import './pushdown.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { PushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Row, Col, IconClose, Container } from '../../importCL';
import SlideDown from '../SlideDown/SlideDown';


export interface IPushdownComponentProps {
    header?: string;
    description?: string;
    message?: string[];
    pushdownCode?: string;
    color?: string;
    isOpen?: boolean;
    fieldErrors?: string[][];
}

const Pushdown = memo((
    props: IPushdownComponentProps,
): JSX.Element => {

    const { closePushdown } = useContext(PushdownContext);
    const [hidden, setHidden] = useState<boolean>(true);
    const selfRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLParagraphElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const iconError = require('./PushdownIcons/pushdown-error.svg').default as string;
    const iconWarning = require('./PushdownIcons/pushdown-warning.svg').default as string;
    const iconNotification = require('./PushdownIcons/pushdown-notification.svg').default as string;

    const handleCloseButtonClick = () => {
        closePushdown();
    };

    const retrieveIcon = () => {
        const COLORS = {
            error: iconError,
            warning: iconWarning,
            notification: iconNotification,
        };
        const src = COLORS[props.color || 'notification'];

        return <img src={src} alt='Icon' />;
    };

    useEffect(() => {
        if (props.isOpen) {
            window.scroll(0, 0);
            setTimeout(() => {
                setHidden(false);
            }, 50);
            if (props.message && props.message.length) {
                listRef.current?.setAttribute('data-aa-pushdown-error-msg', props.description || '');
            } else {
                descriptionRef.current?.setAttribute('data-aa-pushdown-error-msg', props.description || '');
            }
            selfRef.current?.dispatchEvent(new CustomEvent('event19'));
        } else {
            setHidden(true);
            descriptionRef.current?.removeAttribute('data-aa-pushdown-error-msg');
            listRef.current?.removeAttribute('data-aa-pushdown-error-msg');
        }
    }, [props.isOpen, props.message, props.description]);

    if (!props.isOpen) {
        return <></>;
    }
    return (
        <SlideDown
            ref={selfRef} 
            id='pushdown'
            data-pushdown-code={props.pushdownCode}
            className={`Container NewPushdown__bg--${props.color} fluid ${hidden ? 'hidden' : ''}`}
            open={!hidden}
            tabIndex={0}
            role='alert'
            aria-live='assertive'
            >
            <Container className='NewPushdown__container'>
                <div className={`NewPushdown__${props.color}Icon`}>{retrieveIcon()}</div>
                <Container className='NewPushdown__textContainer'>
                    <Row className='NewPushdown__titleContainer'>
                        <Col className='NewPushdown__titleColumn'>
                            <h5 className={`NewPushdown__title--${props.color}`}>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.header))}</h5>
                        </Col>
                    </Row>
                    <Row className='NewPushdown__contentContainer' tabIndex={0}>
                        <Col className='NewPushdown__content'>
                            <p ref={descriptionRef} className='NewPushdown__contentDescription'>{transformHTMLParserLinks(props.description || '')}</p>
                            {props.message && props.message.length > 0 && <ul ref={listRef} className='NewPushdown__contentList'>
                                {props.message && props.message.map((item, index) => {
                                    return (
                                        <li key={index} className='NewPushdown__contentItem'>
                                            {transformHTMLParserLinks(item)}
                                        </li>
                                    );
                                })}
                            </ul>}
                        </Col>
                    </Row>
                </Container>
                <button className='NewPushdown__iconContainer' onClick={handleCloseButtonClick}>
                    {props.color === 'notification' && <IconClose size='utility'/>}
                </button>
            </Container>
        </SlideDown>
    );
});

export default Pushdown;
